/**
 * Таблица рейтинга: общая и дружеская рисуются одинаково.
 *
 * Две таблицы подряд, различающиеся вёрсткой, читались бы как два разных
 * измерения — а измерение одно и то же, различается лишь круг участников.
 *
 * Своя строка отмечена словом, а не одной заливкой: цвет единственным
 * носителем смысла быть не может.
 */

import type { Translate } from '@type2x/i18n';
import type { LeaderboardBoard, LeaderboardKind } from '@type2x/feature-leaderboard';

/**
 * Виды, у которых измерение не видно ни в одном обычном столбце.
 *
 * У Skill это скорость, у Accuracy — точность, у Progress — рейтинг: они уже
 * на виду, и повторять их отдельным столбцом незачем. Регулярность и
 * недельный рост не видно нигде, и без них порядок читается как случайный.
 */
const MEASURED_KINDS = [
  'consistency',
  'weekly-growth',
] as const satisfies readonly LeaderboardKind[];

/** Вид рейтинга, у которого есть своя колонка измеренного. */
type MeasuredKind = (typeof MEASURED_KINDS)[number];

/** Меряет ли этот вид что-то своей колонкой. */
function isMeasured(kind: LeaderboardKind): kind is MeasuredKind {
  return (MEASURED_KINDS as readonly LeaderboardKind[]).includes(kind);
}

/** Дата снимка словами: без секунд и часового пояса читателя. */
function computedAt(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ');
}

export interface BoardTableProps {
  readonly t: Translate;
  readonly board: LeaderboardBoard;
  /** Заголовок над таблицей; без него таблица одна на экране. */
  readonly title?: string;
}

export function BoardTable({ t, board, title }: BoardTableProps): React.JSX.Element {
  // Подпись собирается только для видов со своей колонкой: у остальных её
  // некуда поставить, и ключа для них в словаре нет.
  const measureLabel = isMeasured(board.kind) ? t(`leaderboard.column.metric.${board.kind}`) : null;

  return (
    <>
      {title !== undefined && <h4 className="t2x-quests__title">{title}</h4>}
      <div className="t2x-report__scroll">
        <table className="t2x-report">
          <caption className="t2x-muted">
            {t('leaderboard.computed', {
              moment: computedAt(board.computedAt),
              version: board.version,
            })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('leaderboard.column.rank')}</th>
              <th scope="col">{t('leaderboard.column.name')}</th>
              <th scope="col">{t('leaderboard.column.rating')}</th>
              <th scope="col">{t('leaderboard.column.wpm')}</th>
              <th scope="col">{t('leaderboard.column.accuracy')}</th>
              {measureLabel !== null && <th scope="col">{measureLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {board.rows.map((row) => (
              <tr key={row.rank} className={row.isYou ? 't2x-leaderboard__you' : undefined}>
                <td data-label={t('leaderboard.column.rank')}>{row.rank}</td>
                <td data-label={t('leaderboard.column.name')}>
                  {row.displayName}
                  {row.isYou && <span className="t2x-badge"> {t('leaderboard.you')}</span>}
                </td>
                <td data-label={t('leaderboard.column.rating')}>{row.rating}</td>
                <td data-label={t('leaderboard.column.wpm')}>{Math.round(row.verifiedWpm)}</td>
                <td data-label={t('leaderboard.column.accuracy')}>
                  {Math.round(row.accuracy * 100)}%
                </td>
                {measureLabel !== null && (
                  <td data-label={measureLabel}>{Math.round(row.metric * 100)}%</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
